// Blog API Service
// API Base URL: https://blog.cloudvantage.com.ng
const BLOG_API_BASE_URL = "https://blog.cloudvantage.com.ng/api";

// ============================================
// TOKEN AND ADMIN MANAGEMENT
// ============================================
const getToken = () => localStorage.getItem("adminToken");
const setToken = (token) => localStorage.setItem("adminToken", token);
const removeToken = () => localStorage.removeItem("adminToken");

const setAdmin = (admin) =>
    localStorage.setItem("adminData", JSON.stringify(admin));
const getAdmin = () => {
    const admin = localStorage.getItem("adminData");
    return admin ? JSON.parse(admin) : null;
};
const removeAdmin = () => localStorage.removeItem("adminData");

// ============================================
// AUTH API
// ============================================
export const authAPI = {
    login: async (credentials) => {
        const response = await fetch(`${BLOG_API_BASE_URL}/auth/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(credentials),
        });
        const data = await response.json();

        if (data.token) {
            setToken(data.token);
            setAdmin(data.admin);
            return { success: true, ...data };
        }

        return { success: false, message: data.message || "Login failed" };
    },

    logout: () => {
        removeToken();
        removeAdmin();
        return { success: true };
    },

    getProfile: async () => {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/auth/profile`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return await response.json();
    },

    isAuthenticated: () => {
        return !!getToken();
    },

    getCurrentAdmin: () => {
        return getAdmin();
    },
};

// ============================================
// BLOG API
// ============================================
export const getBlogs = async (params = {}) => {
    try {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString
            ? `${BLOG_API_BASE_URL}/blogs?${queryString}`
            : `${BLOG_API_BASE_URL}/blogs`;

        const response = await fetch(url);
        const result = await response.json();

        if (result && Array.isArray(result.data)) return result.data;
        if (Array.isArray(result)) return result;

        return [];
    } catch (error) {
        console.error("Error fetching blogs:", error);
        return [];
    }
};

export const getBlogById = async (id) => {
    try {
        const response = await fetch(`${BLOG_API_BASE_URL}/blogs/${id}`);
        const result = await response.json();
        if (result && result.data) return result.data;
        return result;
    } catch (error) {
        console.error("Error fetching blog:", error);
        throw error;
    }
};

export const getBlogBySlug = async (slug) => {
    try {
        const response = await fetch(`${BLOG_API_BASE_URL}/blogs/slug/${slug}`);
        const result = await response.json();
        if (result && result.data) return result.data;
        const message = result?.message || "Failed to load blog";
        throw new Error(message);
    } catch (error) {
        console.error("Error fetching blog by slug:", error);
        throw error;
    }
};

export const createBlog = async (blogData) => {
    try {
        const token = getToken();
        const formData = new FormData();

        if (blogData.featuredImage) {
            formData.append("featuredImage", blogData.featuredImage);
        }

        if (blogData.images && Array.isArray(blogData.images)) {
            blogData.images.forEach((img) => {
                formData.append("images", img);
            });
        }

        Object.keys(blogData).forEach((key) => {
            if (
                key !== "featuredImage" &&
                key !== "images" &&
                blogData[key] !== null &&
                blogData[key] !== undefined
            ) {
                formData.append(key, blogData[key]);
            }
        });

        const response = await fetch(`${BLOG_API_BASE_URL}/blogs`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
        return await response.json();
    } catch (error) {
        console.error("Error creating blog:", error);
        throw error;
    }
};

export const updateBlog = async (id, blogData) => {
    try {
        const token = getToken();
        const formData = new FormData();

        if (blogData.featuredImage) {
            formData.append("featuredImage", blogData.featuredImage);
        }

        if (blogData.images && Array.isArray(blogData.images)) {
            blogData.images.forEach((img) => {
                formData.append("images", img);
            });
        }

        Object.keys(blogData).forEach((key) => {
            if (
                key !== "featuredImage" &&
                key !== "images" &&
                blogData[key] !== null &&
                blogData[key] !== undefined
            ) {
                formData.append(key, blogData[key]);
            }
        });

        const response = await fetch(`${BLOG_API_BASE_URL}/blogs/${id}`, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
        return await response.json();
    } catch (error) {
        console.error("Error updating blog:", error);
        throw error;
    }
};

export const deleteBlog = async (id) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/blogs/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return await response.json();
    } catch (error) {
        console.error("Error deleting blog:", error);
        throw error;
    }
};

export const togglePublishBlog = async (id) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/blogs/${id}/publish`, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return await response.json();
    } catch (error) {
        console.error("Error toggling publish:", error);
        throw error;
    }
};

export const getAdminBlogs = async () => {
    try {
        const token = getToken();
        const res = await fetch(`${BLOG_API_BASE_URL}/blogs/admin`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch admin blogs");
        const result = await res.json();
        if (result && Array.isArray(result.data)) return result.data;
        if (Array.isArray(result)) return result;
        return [];
    } catch (error) {
        console.error("Error fetching admin blogs:", error);
        throw error;
    }
};

// ============================================
// CATEGORY API
// ============================================
export const getCategories = async () => {
    try {
        const response = await fetch(`${BLOG_API_BASE_URL}/categories`);
        const result = await response.json();

        if (result && Array.isArray(result.data)) return result.data;
        if (Array.isArray(result)) return result;

        return [];
    } catch (error) {
        console.error("Error fetching categories:", error);
        return [];
    }
};

export const createCategory = async (categoryData) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/categories`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(categoryData),
        });
        return await response.json();
    } catch (error) {
        console.error("Error creating category:", error);
        throw error;
    }
};

export const deleteCategory = async (id) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/categories/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return await response.json();
    } catch (error) {
        console.error("Error deleting category:", error);
        throw error;
    }
};

// ============================================
// AUTHOR API
// ============================================
export const getAuthors = async () => {
    try {
        const response = await fetch(`${BLOG_API_BASE_URL}/authors`);
        const result = await response.json();

        if (result && Array.isArray(result.data)) return result.data;
        if (Array.isArray(result)) return result;

        return [];
    } catch (error) {
        console.error("Error fetching authors:", error);
        return [];
    }
};

export const createAuthor = async (authorData) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/authors`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(authorData),
        });
        return await response.json();
    } catch (error) {
        console.error("Error creating author:", error);
        throw error;
    }
};

export const deleteAuthor = async (id) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/authors/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return await response.json();
    } catch (error) {
        console.error("Error deleting author:", error);
        throw error;
    }
};

// ============================================
// SUBSCRIBERS API
// ============================================
export const subscribeToBlog = async (email) => {
    try {
        const response = await fetch(`${BLOG_API_BASE_URL}/subscribers`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
        });
        return await response.json();
    } catch (error) {
        console.error("Error subscribing:", error);
        return { success: false, message: error.message || "Failed to subscribe" };
    }
};

export const getSubscribers = async () => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/subscribers`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch subscribers");
        const result = await response.json();
        if (Array.isArray(result?.data)) return result.data;
        return [];
    } catch (error) {
        console.error("Error fetching subscribers:", error);
        return [];
    }
};

export const deleteSubscriber = async (id) => {
    try {
        const token = getToken();
        const response = await fetch(`${BLOG_API_BASE_URL}/subscribers/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
        return await response.json();
    } catch (error) {
        console.error("Error deleting subscriber:", error);
        return { success: false, message: error.message };
    }
};

export { BLOG_API_BASE_URL };